import React, {useEffect, useMemo, useState} from 'react';
import tradingService from '../services/tradingService';
import ModalShell from './ModalShell';
import {isTruthy} from '../utils/boolean';

const isCanceledRequest = (err) => {
  if (!err) return false;
  return (
    err?.name === 'AbortError' || // fetch / some libs
    err?.name === 'CanceledError' || // axios v1
    err?.code === 'ERR_CANCELED' || // axios v1
    String(err?.message || '').toLowerCase() === 'canceled'
  );
};

export default function AccountStrategiesSection({ number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [strategies, setStrategies] = useState([]);

  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [deactivatingStrategyId, setDeactivatingStrategyId] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('New strategy');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const page = useMemo(() => Math.floor(offset / Math.max(1, limit)) + 1, [offset, limit]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count || 0) / Math.max(1, limit))),
    [count, limit]
  );

  const canPrev = offset > 0;
  const canNext = offset + limit < (count || 0);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError('');

        // Uses: tradingService.accounts.strategies.getStrategiesForAccount
        const res = await tradingService.accounts.strategies.getStrategiesForAccount({
          number,
          limit,
          offset,
          signal: ac.signal,
        });

        if (!alive) return;

        // API envelope shape is consistent with other sections: { result, error, data: { ... } }
        const payload = res?.data ?? {};

        // Be tolerant to naming differences across backends
        const list =
          payload?.strategies ??
          payload?.results ??
          payload?.items ??
          payload?.data?.strategies ??
          payload?.data?.results ??
          [];

        setStrategies(Array.isArray(list) ? list : []);
        setCount(Number(payload?.count ?? payload?.total ?? 0));
      } catch (err) {
        if (!alive || isCanceledRequest(err) || ac.signal.aborted) return;

        setStrategies([]);
        setCount(0);
        setError(err?.message || 'Failed to load strategies');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [number, limit, offset, reloadNonce]);

  const onDeactivate = async (strategy) => {
    const shortId = strategy?.short_id;
    if (!shortId) {
      setError('Cannot deactivate: strategy id is missing');
      return;
    }

    const ok = window.confirm('Deactivate this strategy? This will also deactivate its subscriptions.');
    if (!ok) return;

    try {
      setError('');
      setDeactivatingStrategyId(String(shortId));

      await tradingService.strategies.deactivate({ strategyId: shortId });
      setReloadNonce((n) => n + 1);
    } catch (err) {
      if (isCanceledRequest(err)) return;
      setError(err?.message || 'Failed to deactivate strategy');
    } finally {
      setDeactivatingStrategyId('');
    }
  };

  const openCreate = () => {
    setCreateName('New strategy');
    setCreateDescription('');
    setCreateError('');
    setCreating(false);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setCreateError('');
  };

  const submitCreate = async (e) => {
    e.preventDefault();

    const name = (createName ?? '').trim();
    const description = (createDescription ?? '').trim();

    if (!name) {
      setCreateError('Strategy name is required');
      return;
    }

    if (!number) {
      setCreateError('Account number is missing');
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      await tradingService.strategies.upsert({
        payload: {
          account_number: number,
          name,
          ...(description ? { description } : null),
        },
      });

      setCreateOpen(false);
      setOffset(0);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      if (isCanceledRequest(err)) return;
      setCreateError(err?.message || 'Failed to create strategy');
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="account-details__section">
      <div className="account-details__section-header">
        <div>
          <div className="account-details__section-title">Strategies</div>
          <div className="account-details__section-subtitle">
            {count ? (
              <>
                Showing {offset + 1}–{Math.min(offset + limit, count)} of {count}
              </>
            ) : (
              'Strategies for this account.'
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="account-details__btn account-details__btn--primary"
            onClick={openCreate}
            disabled={!number || loading}
            title="Create a strategy for this account"
          >
            + Create strategy
          </button>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Page size</span>
            <select
              value={String(limit)}
              onChange={(e) => {
                const next = Math.max(1, Number(e.target.value) || 10);
                setOffset(0);
                setLimit(next);
              }}
              className="account-details__control"
              style={{ width: 110 }}
              disabled={loading}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </label>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
            disabled={!canPrev || loading}
          >
            Prev
          </button>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setOffset((o) => o + limit)}
            disabled={!canNext || loading}
          >
            Next
          </button>

          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Page {page} / {totalPages}
          </div>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setReloadNonce((n) => n + 1)}
            disabled={loading}
            title="Reload current page"
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="account-details__error">{error}</div> : null}
      {loading ? <div>Loading…</div> : null}

      {!loading && !error ? (
        strategies.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Strategy</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Active</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {strategies.map((s, idx) => {
                  const active = isTruthy(s?.is_active);
                  const busy =
                    String(deactivatingStrategyId) &&
                    String(deactivatingStrategyId) === String(s?.short_id);

                  // allow only Active -> Inactive (one-way)
                  const canToggleOff = active && !loading && !busy;

                  const tryDeactivateFromToggle = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!canToggleOff) return;
                    onDeactivate(s);
                  };

                  return (
                    <tr key={`${s?.short_id ?? s?.id ?? 'strategy'}:${idx}`}>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span>{s?.name ?? s?.label ?? '—'}</span>
                        </div>
                      </td>

                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <label
                          className={`account-details__toggle ${!canToggleOff ? 'is-disabled' : ''}`}
                          role={canToggleOff ? 'button' : undefined}
                          tabIndex={canToggleOff ? 0 : -1}
                          aria-disabled={!canToggleOff}
                          title={
                            active
                              ? busy
                                ? 'Deactivating…'
                                : 'Click to deactivate (one-way)'
                              : 'Inactive'
                          }
                          onClick={tryDeactivateFromToggle}
                          onKeyDown={(e) => {
                            if (!canToggleOff) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              tryDeactivateFromToggle(e);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            readOnly
                            disabled={!canToggleOff}
                            aria-label="Strategy active"
                          />
                          <span className="account-details__toggle-ui" aria-hidden="true" />
                        </label>
                      </td>

                      <td style={{ padding: '10px 8px' }}>{s?.description ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No strategies found.</div>
        )
      ) : null}

      <ModalShell
        title="Create strategy"
        open={createOpen}
        onClose={closeCreate}
        footer={
          <>
            <button type="button" onClick={closeCreate} disabled={creating}>
              Cancel
            </button>
            <button type="submit" form="create-strategy-form" disabled={creating || !createName.trim()}>
              {creating ? 'Creating…' : 'Create strategy'}
            </button>
          </>
        }
      >
        <form id="create-strategy-form" onSubmit={submitCreate} className="account-details__keys-form">
          <label>
            <div className="account-details__field-title">Account</div>
            <input value="Current account" disabled className="account-details__control"/>
          </label>

          <label>
            <div className="account-details__field-title">Strategy name</div>
            <input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              disabled={creating}
              autoComplete="off"
              className="account-details__control"
              placeholder="e.g. Conservative BTC strategy"
            />
          </label>

          <label>
            <div className="account-details__field-title">Description</div>
            <textarea
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              disabled={creating}
              className="account-details__control"
              placeholder="Optional notes about this strategy"
              rows={4}
            />
          </label>

          {createError ? <div className="account-details__save-error">{createError}</div> : null}
        </form>
      </ModalShell>
    </section>
  );
}
